// Карточка землетрясения: всё, что о событии известно.
//
// Одна карточка на два состояния, как у TrackPointCard:
//  • не закреплена — читалка при наведении, ПРОЗРАЧНА ДЛЯ МЫШИ. Наползая на
//    курсор, она уводила бы его с точки и гасила сама себя;
//  • закреплена кликом — ловит мышь, показывает крестик и ссылку на USGS,
//    и висит, пока крестик не нажмут.
import { earthquakeCardView, type EarthquakeProps } from './earthquakeCardView'
import { getLang, useT } from '../i18n'
import { MapCallout } from '../map/MapCallout'

interface Props {
  /** Эпицентр: к нему карточка и привязана (см. MapCallout). */
  anchor: { lng: number; lat: number }
  props: EarthquakeProps
  pinned?: boolean
  onClose: () => void
}

export function EarthquakeCard({ anchor, props, pinned = false, onClose }: Props) {
  const t = useT()
  const view = earthquakeCardView(props, getLang())

  return (
    <MapCallout anchor={anchor} width={256} pinned={pinned}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-foam">{view.title}</div>
        {pinned && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-mist transition-colors hover:bg-haze hover:text-foam"
          >
            ×
          </button>
        )}
      </div>

      {/* Описание места от USGS — как есть, на английском: это данные, а не
          интерфейс, и переводить их нам нечем. */}
      {view.place && <div className="mt-0.5 text-[11px] leading-snug text-mist">{view.place}</div>}

      {/* Действует ли ещё влияние. Ради этого круг и на карте: событие старше
          срока восстановления вероятность улова уже не правит. */}
      {view.active !== null && (
        <div
          className={`mt-2 rounded-lg border px-2.5 py-1 text-[12px] ${
            view.active ? 'border-buoy text-buoy' : 'border-hull text-faint'
          }`}
        >
          {t(view.active ? 'models.quake.active' : 'models.quake.passed')}
        </div>
      )}

      <dl className="mt-2 space-y-1 text-[12px]">
        {view.rows.map(row => (
          <div key={row.labelKey} className="flex items-baseline justify-between gap-2">
            <dt className="shrink-0 text-faint">{t(row.labelKey)}</dt>
            <dd className="tnum min-w-0 truncate text-right font-medium text-foam">{row.value}</dd>
          </div>
        ))}
      </dl>

      {pinned ? (
        view.url && (
          <a
            href={view.url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 block rounded-lg border border-hull px-3 py-1.5 text-center text-xs text-foam transition-colors hover:border-sonar hover:bg-haze"
          >
            {t('models.quake.source')}
          </a>
        )
      ) : (
        // Читалка мышь не ловит — ссылку не показываем: она была бы видна, но
        // не нажималась. Клик по событию закрепит карточку вместе с ней.
        <div className="mt-2 text-[11px] text-faint">{t('models.quake.clickToPin')}</div>
      )}
    </MapCallout>
  )
}
